import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Sparkles } from 'lucide-react';
import React from 'react';
import { useVault } from '../../context/VaultContext';
import { fmtFull } from '../../lib/format';

/**
 * All stateful logic for the vault panel lives in this hook so the
 * rendering component stays declarative.
 */
export default function useVaultForm() {
  const {
    pusdBalance,
    ypusdBalance,
    deposit,
    withdraw,
    previewDeposit,
    previewWithdraw,
    apy,
    exchangeRate,
  } = useVault();

  const [mode, setMode] = useState('deposit');
  const [amount, setAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [justSucceeded, setJustSucceeded] = useState(false);

  const parsed = Number(amount) || 0;
  const maxAvailable = mode === 'deposit' ? pusdBalance : ypusdBalance;

  const preview = useMemo(() => {
    if (mode === 'deposit') return previewDeposit(parsed);
    return previewWithdraw(parsed);
  }, [parsed, mode, previewDeposit, previewWithdraw]);

  const changeMode = useCallback((next) => {
    setMode(next);
    setAmount('');
  }, []);

  const validationMessage = useMemo(() => {
    if (parsed <= 0) return null;
    if (parsed > maxAvailable) {
      return `Insufficient ${mode === 'deposit' ? 'PUSD' : 'yPUSD'} balance`;
    }
    return null;
  }, [parsed, maxAvailable, mode]);

  const submit = useCallback(async () => {
    if (parsed <= 0) {
      toast.error('Enter an amount greater than 0');
      return;
    }
    if (parsed > maxAvailable) {
      toast.error(`Insufficient ${mode === 'deposit' ? 'PUSD' : 'yPUSD'} balance`);
      return;
    }
    setSubmitting(true);

    try {
      if (mode === 'deposit') {
        const res = await deposit(parsed);
        if (!res?.ok) {
          const reasonMsg =
            res?.reason === 'INSUFFICIENT_BALANCE'
              ? 'Insufficient PUSD balance'
              : res?.reason === 'RACE_CONDITION'
                ? 'Balance changed before the transaction landed — try again.'
                : 'Deposit failed. Please try again.';
          toast.error(reasonMsg);
          return;
        }
        toast.success('Stake successful', {
          description: res.signature
            ? `Minted ${fmtFull(res.ypusdOut)} yPUSD · on-chain memo: ${res.signature.slice(0, 8)}…`
            : `Minted ${fmtFull(res.ypusdOut)} yPUSD · 1 atomic tx`,
          icon: <Sparkles size={16} />,
        });
      } else {
        // Withdraw stays simulated — preserve the original fake-signing delay.
        await new Promise((r) => setTimeout(r, 1400));
        const res = withdraw(parsed);
        if (!res) {
          toast.error('Withdrawal failed.');
          return;
        }
        toast.success('Withdrawal complete', {
          description: `Redeemed ${fmtFull(res.pusdOut)} PUSD (incl. yield)`,
          icon: <Sparkles size={16} />,
        });
      }

      setAmount('');
      setJustSucceeded(true);
      setTimeout(() => setJustSucceeded(false), 1600);
    } catch (err) {
      // Wallet rejection, RPC failure, confirmation timeout, etc.
      console.error('[palment] vault action failed', err);
      const msg = (err && (err.message || err.toString())) || 'Unknown error';
      const rejected = /reject|denied|user|declin/i.test(msg);
      toast.error(rejected ? 'Transaction rejected in wallet' : 'Transaction failed', {
        description: rejected ? undefined : msg.slice(0, 160),
      });
    } finally {
      setSubmitting(false);
    }
  }, [parsed, maxAvailable, mode, deposit, withdraw]);

  return {
    // state
    mode,
    amount,
    submitting,
    justSucceeded,
    parsed,
    preview,
    maxAvailable,
    apy,
    exchangeRate,
    pusdBalance,
    ypusdBalance,
    validationMessage,
    // actions
    setAmount,
    changeMode,
    submit,
  };
}
